import { useMemo } from 'react';
import _ from 'lodash';

import { User } from '@/portainer/users/types';
import { Team } from '@/react/portainer/users/teams/types';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';
import {
  UserAccessViewModel,
  TeamAccessViewModel,
} from '@/portainer/models/access';
import { getKubernetesRBACInitialValuesFromPolicy } from '@/react/portainer/policies/components/kubernetes/rbac/useKubernetesRBACInitialValues';

import { parseNamespaceAccessPolicy } from './parseNamespaceAccessPolicy';
import { useNamespaceAccessPolicy } from './useNamespaceAccessPolicy';

type NamespaceAccesses = {
  authorizedUsersAndTeams: Array<UserAccessViewModel | TeamAccessViewModel>;
  policyId: number;
  policyName: string;
  hasPolicyApplied: boolean;
  isLoading: boolean;
};

/**
 * Extract namespace access information (both policy-based and ConfigMap-based)
 * Similar to useAccesses for environment access - handles all data transformation
 */
export function useNamespaceAccesses(namespaceName: string): NamespaceAccesses {
  const environmentId = useEnvironmentId();
  const environmentQuery = useEnvironment(environmentId);
  const environment = environmentQuery.data;

  // Reuse the same pattern as getRBACPolicy from useAccesses
  const policyId = environment?.Policies?.k8sRBACPolicy?.policyID;
  const policyName = environment?.Policies?.k8sRBACPolicy?.policyName;

  const policyQuery = usePolicy(policyId ?? 0);
  const usersQuery = useUsers(false, environmentId, !!environmentId);
  const teamsQuery = useTeams(false, environmentId, {
    enabled: !!environmentId,
  });
  const rolesQuery = useRbacRoles({
    select: (roles) => _.orderBy(roles, 'Priority', 'asc'),
  });
  const { accessList } = getKubernetesRBACInitialValuesFromPolicy(
    usersQuery.data || [],
    teamsQuery.data || [],
    rolesQuery.data || [],
    policyQuery.data ?? undefined
  );
  const accessPolicyQuery = useNamespaceAccessPolicy(
    environmentId,
    namespaceName
  );

  return useMemo(() => {
    const users = usersQuery.data || [];
    const teams = teamsQuery.data || [];
    const roles = rolesQuery.data || [];

    const hasPolicyApplied = !!(policyId && policyName && policyQuery.data);

    // When policy is applied, show only policy-based access (or empty if namespace not in policy)
    // Otherwise, show database-based access
    const authorizedUsersAndTeams = hasPolicyApplied
      ? getPolicyAccesses(accessList, namespaceName, users, teams)
      : parseNamespaceAccessPolicy(
          accessPolicyQuery.data,
          users,
          teams,
          roles
        ).flatMap((access) => {
          if (access.type === 'user') {
            const user = users.find((u) => u.Id === access.id);
            return user
              ? [new UserAccessViewModel({ ...user, Role: access.role })]
              : [];
          }

          const team = teams.find((t) => t.Id === access.id);
          return team
            ? [new TeamAccessViewModel({ ...team, Role: access.role })]
            : [];
        });

    return {
      authorizedUsersAndTeams,
      policyId: hasPolicyApplied ? policyId : 0,
      policyName: hasPolicyApplied ? policyName : '',
      hasPolicyApplied,
      isLoading:
        environmentQuery.isLoading ||
        usersQuery.isLoading ||
        teamsQuery.isLoading ||
        accessPolicyQuery.isLoading,
    };
  }, [
    policyId,
    policyName,
    policyQuery.data,
    accessList,
    usersQuery.data,
    usersQuery.isLoading,
    teamsQuery.data,
    teamsQuery.isLoading,
    rolesQuery.data,
    accessPolicyQuery.data,
    accessPolicyQuery.isLoading,
    environmentQuery.isLoading,
    namespaceName,
  ]);
}

/**
 * Extract policy-based access for this namespace
 */
function getPolicyAccesses(
  accessList: ReturnType<
    typeof getKubernetesRBACInitialValuesFromPolicy
  >['accessList'],
  namespaceName: string,
  users: User[],
  teams: Team[]
): Array<UserAccessViewModel | TeamAccessViewModel> {
  return accessList
    .filter(
      (access) => access.namespaces && access.namespaces.includes(namespaceName)
    )
    .flatMap((access) => {
      if (access.Type === 'user') {
        const user = users.find((u) => u.Id === access.PartyId);
        return user
          ? [new UserAccessViewModel({ ...user, Role: access.Role })]
          : [];
      }

      const team = teams.find((t) => t.Id === access.PartyId);
      return team
        ? [new TeamAccessViewModel({ ...team, Role: access.Role })]
        : [];
    });
}
